@if (config.isLanding && submitting) {
  <div class="widget-loader-overlay" aria-live="polite" aria-busy="true">
    <div class="widget-loader-backdrop"></div>
    <div class="widget-loader-content">
      <div class="widget-spinner" role="status" aria-label="Loading"></div>
      <div class="widget-loader-text">Loading, please wait…</div>
    </div>
  </div>
}

<div class="registration-page">
  <div class="registration-card">
    <!-- Heading -->
    <div class="pb-2">
      <h2 class="mb-2 text-center fw-bold">Start Accepting Payments</h2>
      <p class="text-center text-secondary">Complete your registration in about 2 minutes.</p>
    </div>

    @if (clientId !== 'ttpoi-web') {
      <p class="text-center text-muted mb-4" style="padding: 0 1.5rem;">Let's get your business ready for Tap to Pay on
        iPhone. Please fill in the details below.</p>
    }

    <form class="form-section" [formGroup]="createSubmitForm" (ngSubmit)="onSubmit()">

      <!-- Existing Merchant Card Selection (ttpoi-web only, non-landing) -->
      @if (clientId === 'ttpoi-web' && !config.isLanding) {
        <div class="row">
          <div class="col-12 mb-3">
            <div class="mb-4">
              <label class="form-label fw-semibold">{{ "registration.label.existingYes" | translate }}</label>
              <div class="merchant-type-selector">
                <label class="merchant-type-card"
                       [class.selected]="createSubmitForm.get('existingMerchant')?.value === 'Yes'">
                  <div class="merchant-type-card-content">
                    <img [src]="asset('assets/images/icons/person_circle.svg')" alt="" class="merchant-type-icon"
                         aria-hidden="true">
                    <div class="merchant-type-info">
                      <span class="merchant-type-title">Existing Merchant</span>
                      <span class="merchant-type-desc">I'm an existing ADAPTIS merchant.</span>
                    </div>
                  </div>
                  <input
                    type="radio"
                    formControlName="existingMerchant"
                    value="Yes"
                    class="merchant-type-radio"
                  >
                </label>
                <label class="merchant-type-card"
                       [class.selected]="createSubmitForm.get('existingMerchant')?.value === 'No'">
                  <div class="merchant-type-card-content">
                    <img [src]="asset('assets/images/icons/storefront_circle.svg')" alt="" class="merchant-type-icon"
                         aria-hidden="true">
                    <div class="merchant-type-info">
                      <span class="merchant-type-title">New Merchant</span>
                      <span class="merchant-type-desc">I'm new to ADAPTIS.</span>
                    </div>
                  </div>
                  <input
                    type="radio"
                    formControlName="existingMerchant"
                    value="No"
                    class="merchant-type-radio"
                  >
                </label>
              </div>
              @if (hasError('existingMerchant')) {
                <div class="text-danger mt-1">Existing Merchant Selection is required.</div>
              }
            </div>
          </div>
        </div>
      }

      <!-- Non-ttpoi-web: Registration Details section -->
      @if (clientId !== 'ttpoi-web') {
        <div class="row">
          <div class="col-md-12">
            <div class="d-flex align-items-center mb-3 mt-2">
              <i class="bi bi-person-circle text-primary me-2 fs-4"></i>
              <h5 class="ps-2 mb-0 fw-semibold">Registration Details</h5>
            </div>
          </div>
        </div>

        <!-- Register via -->
        <div class="row">
          <div class="col-12 mb-3">
            <label class="form-label required">Register via</label>
            <div class="d-flex gap-4">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  id="registerByOnline"
                  formControlName="registerBy"
                  value="Online"
                >
                <label class="form-check-label" for="registerByOnline">Online</label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  id="registerByPhysical"
                  formControlName="registerBy"
                  value="Dealer"
                >
                <label class="form-check-label" for="registerByPhysical">Dealer</label>
              </div>
            </div>
            @if (hasError('registerBy')) {
              <div class="text-danger mt-1">Registration method is required.</div>
            }
          </div>
        </div>

        <!-- State + Dealer Contact Number -->
        <!-- ng-select: no [appendTo] set — dropdown renders inline (Shadow DOM safe). -->
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label required" for="stateSelected">State</label>
            <ng-select class=""
                       id="stateSelected"
                       formControlName="stateSelected"
                       [items]="statesListFiltered"
                       [clearable]="true"
                       [searchable]="true"
                       (search)="onSearch('stateSelected', $event)"
                       bindLabel="name"
                       bindValue="name"
                       placeholder="Select State"
                       [ngClass]="{'is-invalid': submitting &&
                          (createSubmitForm.controls['stateSelected']?.invalid  ||
                          createSubmitForm.controls['stateSelected']?.errors)}">
              <ng-option [value]="null" disabled>Select State</ng-option>

              <ng-template ng-option-tmp let-item="item">
                {{ item.name }}
              </ng-template>

              <ng-template ng-label-tmp let-item="item">
                {{ item.name }}
              </ng-template>
            </ng-select>
            @if (hasError('stateSelected')) {
              <div class="text-danger mt-1">State is required.</div>
            }
          </div>

          <div class="col-md-6 mb-3">
            <label class="form-label required" for="dealerContact">Dealer Contact Number</label>
            <input class="form-control"
                   id="dealerContact"
                   type="tel"
                   formControlName="dealerContact"
                   placeholder="e.g +60123456789"
                   [ngClass]="{ 'is-invalid': submitting && (createSubmitForm.controls['dealerContact']?.invalid || createSubmitForm.controls['dealerContact']?.errors) }"
            >
            @if (hasError('dealerContact', 'required')) {
              <div class="text-danger mt-1">Dealer Contact Number is required.</div>
            }
            @if (hasError('dealerContact', 'pattern')) {
              <div class="text-danger mt-1">Only + and numeric characters are allowed.</div>
            }
            @if (hasError('dealerContact', 'minlength')) {
              <div class="text-danger mt-1">Minimum 8 characters required.</div>
            }
            @if (hasError('dealerContact', 'maxlength')) {
              <div class="text-danger mt-1">Maximum 20 characters allowed.</div>
            }
          </div>
        </div>
      }

      <!-- Company Name & Business Email -->
      @if (clientId !== 'ttpoi-web') {
        <div class="row">
          <div class="col-md-12">
            <hr class="my-3">
            <div class="d-flex align-items-center mb-3">
              <i class="bi bi-building text-primary me-2 fs-4"></i>
              <h5 class="ps-2 mb-0 fw-semibold">{{ 'registration.section.businessInfo' | translate }}</h5>
            </div>
          </div>
        </div>
      }

      <div class="row">
        <!-- Company Name -->
        <div class="col-md-6 mb-3">
          <label class="form-label fw-semibold required" for="companyName">Company Name</label>
          <div class="input-with-icon">
            <span class="input-icon">
              <img [src]="asset('assets/images/icons/company_box.svg')" alt="" aria-hidden="true">
            </span>
            <input class="form-control"
                   id="companyName"
                   type="text"
                   formControlName="companyName"
                   placeholder="Enter your company name"
                   [ngClass]="{ 'is-invalid': submitting && (createSubmitForm.controls['companyName']?.invalid || createSubmitForm.controls['companyName']?.errors) }"
            >
          </div>
          @if (hasError('companyName')) {
            <div class="text-danger mt-1">Company Name is required.</div>
          }
        </div>
        <!-- Email -->
        <div class="col-md-6 mb-3">
          <label class="form-label fw-semibold required" for="registerEmail">Business Email</label>
          <div class="input-with-icon">
            <span class="input-icon">
              <img [src]="asset('assets/images/icons/email_box.svg')" alt="" aria-hidden="true">
            </span>
            <input class="form-control"
                   id="registerEmail"
                   type="email"
                   formControlName="registerEmail"
                   [ngClass]="{ 'is-invalid': submitting && (createSubmitForm.controls['registerEmail']?.invalid || createSubmitForm.controls['registerEmail']?.errors) }"
                   placeholder="Enter your business email"
            >
          </div>
          @if (hasError('registerEmail', 'required')) {
            <div class="text-danger mt-1">Email address is required.</div>
          }
          @if (hasError('registerEmail', 'email')) {
            <div class="text-danger mt-1">Please enter a valid email address.</div>
          }
        </div>
      </div>

      <!-- Country (ttpoi-web only) -->
      @if (clientId === 'ttpoi-web') {
        <div class="row">
          <div class="col-md-12 mb-3">
            <label class="form-label fw-semibold required" for="country">Country</label>
            <div class="input-with-icon ng-select-with-icon">
              <span class="input-icon">
                <img [src]="asset('assets/images/icons/globe_box.svg')" alt="" aria-hidden="true">
              </span>
              <ng-select class=""
                         id="country"
                         formControlName="country"
                         [items]="countriesFiltered"
                         [clearable]="true"
                         (search)="onSearch('country', $event)"
                         (change)="updateSelectionFromId('country', $event)"
                         bindLabel="name"
                         bindValue="id"
                         placeholder="Select a Country"
                         [ngClass]="{'is-invalid': submitting &&
                            (createSubmitForm.controls['country']?.invalid  ||
                            createSubmitForm.controls['country']?.errors)}">
                <ng-option [value]="null" disabled>Select countries...</ng-option>

                <ng-template ng-option-tmp let-item="item">
                  <span [innerHTML]="(item.code || item.iso2) | flagEmoji"></span> {{ item.name }}
                </ng-template>

                <ng-template ng-label-tmp let-item="item">
                  <span class="chip">{{ item.name }}</span>
                </ng-template>
              </ng-select>
            </div>
            @if (hasError('country')) {
              <div class="text-danger mt-1">Country is required.</div>
            }
          </div>
        </div>
      }

      <!-- MSE Selection (ttpoi-web only, hidden in landing when showMseInLanding is false) -->
      @if (clientId === 'ttpoi-web' && (!config.isLanding || showMseInLanding)) {
        <div class="row">
          <div class="col-md-12 mb-3">
            <hr class="my-3">
          </div>
          <div class="col-md-12 mb-3">
            <label class="form-label fw-semibold">Were you referred by an ADAPTIS Sales Executive?</label>
            <div class="referral-options mt-2">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  id="mseNo"
                  name="mseReferral"
                  [checked]="!mseEnabled"
                  (change)="onMseRadioChange(false)"
                >
                <label class="form-check-label" for="mseNo">No, I was not referred</label>
              </div>
              <span class="referral-divider"></span>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  id="mseYes"
                  name="mseReferral"
                  [checked]="mseEnabled"
                  (change)="onMseRadioChange(true)"
                >
                <label class="form-check-label" for="mseYes">Yes, I was referred</label>
              </div>
            </div>
          </div>
        </div>

        @if (mseEnabled) {
          <div class="row">
            <div class="col-md-12 mb-3">
              <label class="form-label required" for="mseDropdown">Sales Executive</label>
              <div class="input-with-icon ng-select-with-icon">
                <span class="input-icon">
                  <img [src]="asset('assets/images/icons/person_circle.svg')" alt="" aria-hidden="true">
                </span>
                <ng-select
                  id="mseDropdown"
                  [items]="mseListFiltered"
                  [clearable]="true"
                  [searchable]="true"
                  bindLabel="display_name"
                  bindValue="id"
                  placeholder="Select a Sales Executive"
                  (change)="onMseSelected($event)"
                >
                  <ng-template ng-option-tmp let-item="item">
                    {{ item.display_name ? item.display_name : (item.last_name && item.last_name !== item.first_name ? item.last_name + ' ' + item.first_name : item.last_name || item.first_name) }}
                  </ng-template>
                  <ng-template ng-label-tmp let-item="item">
                    {{ item.display_name ? item.display_name : (item.last_name && item.last_name !== item.first_name ? item.last_name + ' ' + item.first_name : item.last_name || item.first_name) }}
                  </ng-template>
                </ng-select>
              </div>
            </div>
          </div>
        }
      }

      <!-- Agreements -->
      <div class="row mt-3">
        <div class="col-12 mb-3">
          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              id="userAgreement"
              formControlName="userAgreement"
              (change)="onUserAgreementToggle($event)"
            >
            <label class="form-check-label required" for="userAgreement">
              I have read and agreed to the
              <a href="#" class="text-primary" (click)="openPrivacyNotice($event)">Privacy Notice</a>
              and
              <a href="#" class="text-primary" (click)="openTermsOfService($event)">Terms of Service</a>.
            </label>
          </div>

          @if (agreementStatusMessage) {
            <div class="small mt-2 mb-1" [innerHTML]="agreementStatusMessage"></div>
          }

          @if (userAgreementError) {
            <div class="text-danger mt-1">{{ userAgreementError }}</div>
          }
        </div>
      </div>

      <div class="row pt-2">
        <div class="d-flex">
          <label class="form-label required"></label><span class="ms-1 text-danger">This field is mandatory.</span>
        </div>
      </div>

      <div class="row">
        <p class="text-muted small mt-3">
          {{ 'registration.recaptcha.disclaimer' | translate }}
        </p>
      </div>

      <div class="row">
        <div class="d-flex justify-content-center align-items-center mt-3 text-center">
          <button type="submit" class="btn btn-primary w-100 btn-submit"
                  [disabled]="submitting">
            {{ submitting ? 'Submitting...' : 'Submit application' }}
          </button>
        </div>
        <div class="d-flex justify-content-center align-items-center gap-3 mt-3 text-center">
          @if (submitSuccess) {
            <div class="text-success">Form submitted successfully.</div>
          }
        </div>
      </div>
    </form>
  </div>
</div>

<!-- Policy modal templates -->
<ng-template #tosTpl>
  <app-terms-of-service-template></app-terms-of-service-template>
</ng-template>

<ng-template #ppTpl>
  <app-privacy-notice-template></app-privacy-notice-template>
</ng-template>

@if (config.useOverlayPolicies) {
  <app-policy-overlay #policyOverlay
    [title]="modalTitle"
    [contentTpl]="modalTpl"
    [documentId]="currentAgreementOpened ?? ''"
    (scrolledToEnd)="onAgreementScrolledToEnd()"
    (agreed)="onAgreementAgreed()"
    (closed)="onAgreementModalClosed()">
  </app-policy-overlay>
} @else {
  <app-shared-policy-modal
    [title]="modalTitle"
    [contentTpl]="modalTpl"
    (scrolledToEnd)="onAgreementScrolledToEnd()"
    (agreed)="onAgreementAgreed()"
    (closed)="onAgreementModalClosed()">
  </app-shared-policy-modal>
}
